import React, { useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import {
  Box,
  Typography,
  TextField,
  Button,
  useTheme,
  Stack,
  InputAdornment,
  IconButton,
} from '@mui/material'
import Visibility from '@mui/icons-material/Visibility'
import VisibilityOff from '@mui/icons-material/VisibilityOff'
import { useAuth } from 'auth'
import logger from 'logger'

import InputLabel from 'components/atoms/InputLabel'

const ConfirmResetPassword = ({
  setView,
  state,
  setState,
  fetching,
  error,
  setError,
  setFetching,
  authErrorMapping,
}) => {
  const theme = useTheme()
  const { t } = useTranslation('auth')
  const { AmplifyAuth, authEvent } = useAuth()

  const handleInputChange = (event) => {
    setState((s) => ({ ...s, [event.target.name]: event.target.value }))
  }

  const handleConfirmResetPassword = async (event) => {
    try {
      setFetching(true)
      setError(null)
      if (state.newPassword !== state.newPasswordConfirm) {
        setError('PasswordsNotEqual')
        return
      }
      await AmplifyAuth.completeNewPassword(authEvent, state.newPassword)

      setState((s) => ({
        ...s,
        password: '',
        newPassword: '',
        newPasswordConfirm: '',
      }))

      setView('resetPasswordSuccess')
    } catch (e) {
      if (e.name === 'AuthError') {
        setError(authErrorMapping[e.log] || e.log)
        return
      }
      if (e.code === 'CodeMismatchException') {
        setError(e.code)
        return
      }
      if (e.code === 'ExpiredCodeException') {
        setError(e.code)
        return
      }
      logger.error(e)
      setError(e.code)
    } finally {
      setFetching(false)
    }
  }

  const [showPassword, setShowPassword] = useState(false)

  const handleShowPassword = () => {
    setShowPassword(!showPassword)
  }

  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false)

  const handleShowPasswordConfirm = () => {
    setShowPasswordConfirm(!showPasswordConfirm)
  }

  return (
    <Stack
      width="50%"
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      sx={{
        maxWidth: 400,
      }}
    >
      <Typography variant="h4" sx={{ pb: 1 }}>
        <strong>{t('headers.forceChangePassword.title')}</strong>
      </Typography>

      <Typography
        textAlign="center"
        style={{ color: theme.palette.primary.dark }}
        mb={5}
      >
        {t('messageForceChangePassword', state)}
      </Typography>
      <Box sx={{ width: 1 }}>
        <InputLabel
          label={t('attributes.password.label')}
          inputId="new-password"
          sx={{ textAlign: 'left' }}
        />
        <TextField
          id="new-password"
          size="small"
          fullWidth
          placeholder={t('attributes.password.placeholder')}
          type={showPassword ? 'text' : 'password'}
          name="newPassword"
          value={state.newPassword}
          onChange={handleInputChange}
          disabled={fetching}
          autoComplete="false"
          InputProps={{
            'data-testid': 'forceChangePassword-newPassword',
            autoComplete: '',
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={handleShowPassword}
                  onMouseDown={(e) => e.preventDefault()}
                  edge="end"
                  data-testid="forceChangePassword-toggleViewPassword"
                >
                  {showPassword ? (
                    <VisibilityOff data-testid="forceChangePassword-passwordVisibleOff" />
                  ) : (
                    <Visibility data-testid="forceChangePassword-passwordVisibleOn" />
                  )}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Box>
      <Box mb={5} />
      <Box sx={{ width: 1 }}>
        <InputLabel
          label={t('attributes.passwordConfirm.label')}
          inputId="new-password-confirm"
          sx={{ textAlign: 'left' }}
        />
        <TextField
          id="confirm-reset-password-confirm"
          size="small"
          fullWidth
          placeholder={t('attributes.passwordConfirm.placeholder')}
          type={showPasswordConfirm ? 'text' : 'password'}
          name="newPasswordConfirm"
          value={state.newPasswordConfirm}
          onChange={handleInputChange}
          disabled={fetching}
          autoComplete="false"
          InputProps={{
            'data-testid': 'forceChangePassword-newPasswordConfirm',
            autoComplete: 'false',
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={handleShowPasswordConfirm}
                  onMouseDown={(e) => e.preventDefault()}
                  edge="end"
                  data-testid="forceChangePassword-toggleViewPasswordConfirm"
                >
                  {showPasswordConfirm ? (
                    <VisibilityOff data-testid="forceChangePassword-passwordConfirmVisibleOff" />
                  ) : (
                    <Visibility data-testid="forceChangePassword-passwordConfirmVisibleOn" />
                  )}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Box>
      <Box pt={2}>
        <Typography mb={0} fontSize="80%">
          La password deve rispettare i seguenti criteri:
        </Typography>
        <ul style={{ marginTop: '2px', fontSize: '80%' }}>
          <li>contiene almeno 6 caratteri</li>
          <li>contiene almeno un carattere MAIUSCOLO</li>
          <li>contiene almeno un carattere minuscolo</li>
        </ul>
      </Box>
      <Box mb={2} />
      <Button
        color="primary"
        variant="contained"
        disabled={fetching}
        onClick={handleConfirmResetPassword}
        data-testid="forceChangePassword-button"
      >
        {t('buttons.forceChangePassword')}
      </Button>
      {error && (
        <Typography
          textAlign="center"
          p={[3]}
          style={{ color: theme.palette.error.light }}
          data-testid="forceChangePassword-error"
        >
          {t(`errors.${error}`, state)}
        </Typography>
      )}
      <Box mb={5} />
      <Typography textAlign="center">
        <Trans
          t={t}
          i18nKey="messageChangeAccount"
          components={{
            action: (
              <Typography
                variant="span"
                color="primary"
                data-testid="forceChangePassword-backToSignin"
                style={{
                  cursor: 'pointer',
                  textDecoration: 'underline',
                  fontWeight: 'bold',
                }}
                onClick={() => {
                  setState((s) => ({
                    ...s,
                    confirmCode: '',
                    password: '',
                    passwordConfirm: '',
                  }))
                  setView('signIn')
                }}
              />
            ),
          }}
        />
      </Typography>
    </Stack>
  )
}

export default ConfirmResetPassword
